<template>
  <section
    v-if="fields && items.length"
    :class="rootClass"
    :style="rootStyle"
    :id="rendering?.dataSource"
  >
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div ref="sectionEl" class="c-numbers-section__inner">
        <article v-for="item in items" :key="item.id" class="c-numbers-section__item">
          <FlippedNumber
            v-if="isInView && item.fields.number?.value"
            :key="`${item.id}-${animationCycle}`"
            class="c-numbers-section__number"
            :number="item.fields.number.value"
            :speed="1200"
          />
          <RichText
            v-if="item.fields.description?.value"
            class="c-numbers-section__description"
            :field="item.fields.description"
          />
        </article>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import type {
  NumbersSectionItemFields,
  NumbersSectionProps,
} from '@/types/components/NumbersSection';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

interface NormalizedItem extends ScItem<NumbersSectionItemFields> {
  fields: NumbersSectionItemFields;
}

const props = defineProps<NumbersSectionProps>();
const sectionEl = ref<HTMLElement | null>(null);
const isInView = ref(false);
const animationCycle = ref(0);
let visibilityObserver: IntersectionObserver | null = null;
const fields = computed(() => props.fields);
const rendering = computed(() => props.rendering);

const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});
const tone = computed(() => fields.value?.tone?.value || 'gold');
const rootClass = computed(() => {
  const result = utils.overall.baseComClass(props.fields, 'c-numbers-section');
  result.push(`c-numbers-section--${tone.value}`);
  return result;
});
const rawItems = computed<ScItem<NumbersSectionItemFields>[]>(() => {
  const statItems = utils.sc.getFieldObj(fields.value, 'items');
  return Array.isArray(statItems) ? statItems : [];
});

const items = computed<NormalizedItem[]>(() =>
  rawItems.value.filter((item): item is NormalizedItem => Boolean(item.fields))
);

onMounted(() => {
  if (!sectionEl.value || !window.IntersectionObserver) {
    isInView.value = true;
    animationCycle.value += 1;
    return;
  }

  visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        animationCycle.value += 1;
        isInView.value = true;
      } else {
        isInView.value = false;
      }
    },
    { threshold: 0.1 }
  );
  visibilityObserver.observe(sectionEl.value);
});

onBeforeUnmount(() => {
  visibilityObserver?.disconnect();
});
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-numbers-section {
  padding: 40px 0;
  background: $white;

  &__inner {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 70px 28px;
    max-width: 1440px;
    margin: 0 auto;
  }

  &__item {
    display: flex;
    flex: 0 1 calc((100% - 28px) / 2);
    flex-direction: column;
    align-items: center;
    min-width: 0;
    text-align: center;
  }

  &__number {
    justify-content: center;
    color: #dbc9ac;
    font-size: 32px;
    line-height: 1;
    font-weight: 700;
    letter-spacing: 0;

    .e-flipped-number__card {
      max-height: none;
    }

    .e-flipped-number__card,
    .e-flipped-number__other {
      line-height: 1;
    }
  }

  &__description {
    margin-top: 24px;
    max-width: 300px;
    color: #666666;
    font-size: 18px;
    line-height: 1.42;
    font-weight: 400;

    p {
      margin: 0;
    }
  }

  &--green {
    .c-numbers-section__number {
      color: #6f9877;
    }

    .c-numbers-section__description {
      color: #4e4e4e;
    }
  }

  @include tablet {
    &__inner {
      gap: 72px 36px;
    }

    &__item {
      flex-basis: calc((100% - 36px) / 2);
    }

    &__number {
      font-size: 40px;
    }

    &__description {
      margin-top: 18px;
      font-size: 24px;
      line-height: 1.36;
    }
  }

  @include tablet-landscape {
    &__inner {
      gap: 40px;
    }

    &__item {
      flex: 1 1 0;
    }

    &__number {
      font-size: 40px;
    }

    &__description {
      margin-top: 18px;
      max-width: 250px;
      font-size: 21px;
      line-height: 1.35;
    }
  }
}
</style>
